import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Chip,
  Box,
  CircularProgress,
  Autocomplete,
  Typography,
  IconButton,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { useLeadDetails } from '../../contexts/LeadDetailsContext';

const T = {
  green: '#0a6b3e',
  greenLt: '#e5f5ec',
  greenMd: '#0d8a50',
  ink: '#0f1a13',
  subtle: '#526b5c',
  muted: '#8eaa98',
  bg: '#f5f8f6',
  border: '#dce8e1',
  borderL: '#eaf1ec',
  white: '#ffffff',
  red: '#dc2626',
  redLt: '#fef2f2',
  redBorder: '#fecaca',
  font: "'Inter','DM Sans','Plus Jakarta Sans',sans-serif",
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    fontFamily: T.font,
    fontSize: 13,
    borderRadius: 2,
    bgcolor: T.white,
    '& fieldset': { borderColor: T.borderL },
    '&:hover fieldset': { borderColor: T.border },
    '&.Mui-focused fieldset': { borderColor: T.green },
  },
  '& .MuiInputLabel-root': {
    fontFamily: T.font,
    fontSize: 13,
    color: T.subtle,
    '&.Mui-focused': { color: T.green },
  },
  '& .MuiOutlinedInput-input::placeholder': {
    fontFamily: T.font,
    opacity: 0.65,
  },
};

const prefChipSx = {
  fontFamily: T.font,
  fontSize: 12,
  height: 26,
  bgcolor: T.greenLt,
  color: T.green,
  border: `1px solid ${T.border}`,
  '& .MuiChip-deleteIcon': { color: T.subtle, '&:hover': { color: T.green } },
};

const PreferenceUpdate = ({ open, onClose, leadId }) => {
  const { preferences, fetchLeadPreference, updateLeadPreferences } = useLeadDetails();
  const [loading, setLoading] = useState(false);
  const [propertyPreferences, setPropertyPreferences] = useState([]);
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (preferences?.default_preferences) {
      setPropertyOptions(
        preferences.default_preferences.map((item) => ({
          key: item.type,
          label: item.label,
        }))
      );
    }
  }, [preferences]);

  useEffect(() => {
    if (!open) return;
    setSaveError(null);
    if (open && preferences?.saved_preferences) {
      const savedPrefs = preferences.saved_preferences.preferences || [];
      setPropertyPreferences(
        savedPrefs.map((pref) => {
          if (typeof pref === 'object' && pref.key && pref.label) return pref;

          const matchingOption = preferences.default_preferences?.find(
            (dp) => dp.type === (typeof pref === 'object' ? pref.key || pref.type : pref)
          );

          if (matchingOption) {
            return { key: matchingOption.type, label: matchingOption.label };
          }

          return typeof pref === 'string'
            ? { key: pref, label: pref }
            : { key: pref.key || pref.type || '', label: pref.label || '' };
        })
      );
    }
  }, [open, preferences]);

  const handleSave = async () => {
    setLoading(true);
    setSaveError(null);
    try {
      const keyList = propertyPreferences.map((pref) => pref.key);
      await updateLeadPreferences(leadId, keyList, 'property_preferences');
      await fetchLeadPreference(leadId);
      onClose();
    } catch (err) {
      setSaveError(err?.message || 'Could not save preferences. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: 480,
          width: '100%',
          borderRadius: 3,
          fontFamily: T.font,
          overflow: 'hidden',
          m: 2,
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                bgcolor: T.greenLt,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <TuneOutlinedIcon sx={{ fontSize: 22, color: T.green }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontFamily: T.font, fontSize: 16, fontWeight: 700, color: T.ink, lineHeight: 1.3 }}>
                Property preferences
              </Typography>
              <Typography sx={{ fontFamily: T.font, fontSize: 11.5, color: T.muted, mt: 0.25, lineHeight: 1.45 }}>
                Choose what this lead is looking for — used for matching and filters
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close"
            disabled={loading}
            sx={{ color: T.muted, '&:hover': { color: T.ink } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider sx={{ borderColor: T.borderL }} />

      <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
        <Autocomplete
          multiple
          id="property-preferences"
          options={propertyOptions}
          value={propertyPreferences}
          onChange={(event, newValue) => setPropertyPreferences(newValue)}
          getOptionLabel={(option) => option?.label || ''}
          isOptionEqualToValue={(option, value) => option.key === value.key}
          componentsProps={{
            paper: {
              sx: {
                fontFamily: T.font,
                borderRadius: 2,
                border: `1px solid ${T.borderL}`,
                boxShadow: '0 8px 24px rgba(15, 26, 19, 0.08)',
              },
            },
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip label={option.label} {...getTagProps({ index })} key={option.key} size="small" sx={prefChipSx} />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Preferences"
              variant="outlined"
              placeholder="Search and select preferences"
              sx={fieldSx}
            />
          )}
        />

        {saveError && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 2,
              border: `1px solid ${T.redBorder}`,
              bgcolor: T.redLt,
            }}
          >
            <Typography sx={{ fontFamily: T.font, fontSize: 12.5, color: T.red }}>{saveError}</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          gap: 1,
          justifyContent: 'flex-end',
          borderTop: `1px solid ${T.borderL}`,
          bgcolor: T.bg,
        }}
      >
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            fontFamily: T.font,
            fontSize: 13,
            fontWeight: 500,
            color: T.subtle,
            textTransform: 'none',
            borderRadius: 2,
            px: 2,
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading}
          sx={{
            fontFamily: T.font,
            fontSize: 13,
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 2,
            px: 2.5,
            minWidth: 132,
            bgcolor: T.green,
            color: '#fff',
            boxShadow: 'none',
            '&:hover': { bgcolor: T.greenMd, boxShadow: 'none' },
            '&.Mui-disabled': { bgcolor: T.borderL, color: T.muted },
          }}
        >
          {loading ? <CircularProgress size={22} thickness={4} sx={{ color: '#fff' }} /> : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreferenceUpdate;
